import createCachedSelector from 're-reselect'
import { createSelector } from 'reselect'
import {
  FEATURE_INBOX_MAX_REPORTING_DAYS,
  FEATURE_KB_MAX_REPORTING_DAYS,
  billingUsageKeyForProductFeature,
} from 'ducks/billing/featureTypes'
import { INBOX, KB } from 'ducks/billing/productTypes'
import { emptyObj } from 'util/objects'
import { KB_PAGE } from 'ducks/reports/kb/pages'
import { selectCurrentPage } from 'selectors/location'
import { selectKbProduct } from '../selectKbProduct'
import { selectInboxProduct } from '../selectInboxProduct'

import { selectUsageForProductFeature } from '../usage/selectUsageForProductFeature'
import { selectLocalUsageForProduct } from '../usage/selectLocalUsageForProduct'
import { selectBillingUsageForProduct } from '../usage/selectBillingUsageForProduct'
import { selectLimitForProductFeature } from './selectLimitForProductFeature'
import { byProductFeatureCacheKey } from './byProductFeatureCacheKey'

export const selectInboxFeatures = createSelector(
  selectInboxProduct,
  inboxProduct => inboxProduct.features || emptyObj
)

export const selectKbFeatures = createSelector(
  selectKbProduct,
  kbProduct => kbProduct.features || emptyObj
)

export const selectCanUseProductFeature = createCachedSelector(
  selectLimitForProductFeature,
  selectBillingUsageForProduct,
  selectLocalUsageForProduct,
  (_state, product) => product,
  (_state, _product, feature) => feature,
  (featureLimit, billingUsage, localUsage, product, feature) => {
    if ([undefined, null].includes(featureLimit)) {
      return true
    }

    if (featureLimit === 'unlimited') return true
    if (typeof featureLimit === 'boolean') return featureLimit // no need to check usage

    const billingUsageKey = billingUsageKeyForProductFeature(product, feature)
    if (typeof featureLimit === 'number' && billingUsageKey) {
      // check current usage
      const usageValue =
        localUsage[billingUsageKey] || billingUsage[billingUsageKey]

      return Number(usageValue) < featureLimit
    }

    return true
  }
)(byProductFeatureCacheKey)

export const selectProductFeatureUsage = createCachedSelector(
  selectCanUseProductFeature,
  selectLimitForProductFeature,
  selectUsageForProductFeature,
  (canUseFeature, featureLimit, currentUsage) => ({
    canUseFeature,
    featureLimit,
    currentUsage,
  })
)(byProductFeatureCacheKey)

export const selectMaxReportingDaysForProduct = createSelector(
  selectCurrentPage,
  state =>
    selectLimitForProductFeature(
      state,
      INBOX,
      FEATURE_INBOX_MAX_REPORTING_DAYS
    ),
  state =>
    selectLimitForProductFeature(state, KB, FEATURE_KB_MAX_REPORTING_DAYS),
  (currentPage, inboxMaxReportingDays, kbMaxReportingDays) => {
    return currentPage === KB_PAGE ? kbMaxReportingDays : inboxMaxReportingDays
  }
)
